import gsap from "gsap";
import { useLayoutEffect, useRef } from "react";

type Options = {
  /** Axis the items travel along — `x` for the sidebar column, `y` for the bottom tabs. */
  axis?: "x" | "y";
  /** Travel distance in px. Kept small so the entrance reads as a settle, not a slide-in. */
  distance?: number;
  delay?: number;
};

/**
 * Staggers `[data-nav-item]` children into view on mount. Returns a ref for the
 * nav container. Reduced motion collapses to instant, per DESIGN.md.
 *
 * The entrance only ever *enhances* an already-visible nav. `autoAlpha` sets
 * `visibility: hidden` as its from-state, and GSAP advances on requestAnimationFrame
 * — which browsers pause on a hidden tab. Applying the from-state and then never
 * getting a frame strands the navigation permanently invisible, which is exactly
 * what happened: Calendar, Teams and Settings sat at `opacity: 0` with no tween
 * running. Headless renderers and screenshot tools hit the same path.
 *
 * So: never touch the DOM unless the tab is visible, and if it is backgrounded
 * mid-flight, snap to the end state instead of leaving items part-faded.
 */
export function useNavEntrance<T extends HTMLElement>({
  axis = "x",
  distance = 10,
  delay = 0,
}: Options = {}) {
  const ref = useRef<T>(null);

  // Layout effect so the "from" state is set before the browser paints the nav.
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    const items = root.querySelectorAll<HTMLElement>("[data-nav-item]");
    if (items.length === 0) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Bail before writing any from-state. The nav is already visible; leaving it
    // that way is strictly better than hiding it for a tween that cannot run.
    if (document.visibilityState !== "visible") return;

    let tween: gsap.core.Tween | undefined;

    const ctx = gsap.context(() => {
      tween = gsap.from(items, {
        autoAlpha: 0,
        [axis]: distance,
        duration: 0.2,
        ease: "power2.out",
        stagger: 0.04,
        delay,
        // Hand the elements back to CSS once done, so hover transitions stay untouched.
        clearProps: "transform,opacity,visibility",
      });
    }, root);

    // Backgrounded mid-entrance: finish immediately rather than freeze half-faded.
    const onHide = () => {
      if (document.visibilityState === "hidden") tween?.progress(1);
    };
    document.addEventListener("visibilitychange", onHide);

    return () => {
      document.removeEventListener("visibilitychange", onHide);
      ctx.revert();
    };
  }, [axis, distance, delay]);

  return ref;
}
